import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, RefreshCw, X, Sparkles, AlertCircle } from 'lucide-react';
import useTauriWindowFloor from '../../hooks/useTauriWindowFloor';
import './UpdateNotificationBar.css';

/**
 * UpdateNotificationBar
 *
 * Shows a slim banner at the top of the app when a new version is available,
 * or when the update path itself hit a problem. Only renders inside Tauri
 * (desktop) — invisible in web browser.
 *
 * Flow:
 * 1. On mount, checks for updates via Tauri updater plugin
 * 2. If update available → shows banner with version + "Update now" button
 * 3. User clicks → downloads & installs → prompts restart
 * A failed check or a failed install surfaces inline in this same bar
 * (never a browser alert()) with a Retry action.
 */
export default function UpdateNotificationBar() {
    // Desktop-window sizing is a different concern from "is an update
    // available", but this is the one Tauri-integration component mounted at
    // the root of the main app shell (AppShell.jsx is owned elsewhere this
    // run) — see useTauriWindowFloor for why it lives here.
    useTauriWindowFloor();

    const [update, setUpdate] = useState(null);
    const [status, setStatus] = useState('idle'); // idle | downloading | installing | error | check-error
    const [progress, setProgress] = useState(0);
    const [dismissed, setDismissed] = useState(false);

    // Guards a setState landing after unmount — the initial check runs behind
    // a 5s timer and a network round-trip, and Retry can be clicked and then
    // outlive the component too.
    const cancelledRef = useRef(false);
    useEffect(() => () => { cancelledRef.current = true; }, []);

    const checkForUpdate = useCallback(async () => {
        try {
            const { check } = await import('@tauri-apps/plugin-updater');
            const result = await check();
            if (cancelledRef.current) return;
            if (result?.available) setUpdate(result);
            // A retry that resolves (found an update or confirmed there isn't
            // one) clears any earlier check-error banner.
            setStatus((s) => (s === 'check-error' ? 'idle' : s));
        } catch (err) {
            if (cancelledRef.current) return;
            const msg = err.message || String(err);
            console.debug('[Updater] Check failed:', msg);
            setStatus('check-error');
        }
    }, []);

    useEffect(() => {
        // Only run inside Tauri desktop app
        if (!window.__TAURI_INTERNALS__) return;
        // Check after a short delay so the app loads first
        const timer = setTimeout(checkForUpdate, 5000);
        return () => clearTimeout(timer);
    }, [checkForUpdate]);


    const handleUpdate = useCallback(async () => {
        if (!update || status === 'downloading' || status === 'installing') return;

        try {
            setStatus('downloading');

            // Download and install with progress tracking
            await update.downloadAndInstall((event) => {
                if (event.event === 'Started' && event.data?.contentLength) {
                    setProgress(0);
                } else if (event.event === 'Progress') {
                    setProgress((prev) => {
                        const newProgress = prev + (event.data?.chunkLength || 0);
                        return newProgress;
                    });
                } else if (event.event === 'Finished') {
                    setStatus('installing');
                }
            });

            setStatus('installing');

            // Relaunch the app
            const { relaunch } = await import('@tauri-apps/plugin-process');
            await relaunch();
        } catch (err) {
            const msg = err.message || String(err);
            console.error('[Updater] Install failed:', msg, err);
            setStatus('error');
        }
    }, [update, status]);

    const hasCheckError = status === 'check-error';

    // Don't render in web browser, or if dismissed, or if there's neither an
    // update nor a check failure to report.
    if (!window.__TAURI_INTERNALS__ || dismissed) return null;
    if (!update && !hasCheckError) return null;

    return (
        <div className="update-bar" role="status" aria-live="polite">
            <div className="update-bar__content">
                {hasCheckError ? (
                    <>
                        <AlertCircle size={16} className="update-bar__icon update-bar__icon--error" />
                        <span className="update-bar__message">Couldn't check for updates</span>
                    </>
                ) : (
                    <>
                        <Sparkles size={16} className="update-bar__icon" />
                        <span className="update-bar__message">
                            Alphafocus v{update.version} is available
                            {update.body && (
                                <span className="update-bar__notes"> — {update.body}</span>
                            )}
                        </span>
                    </>
                )}
            </div>

            <div className="update-bar__actions">
                {hasCheckError ? (
                    <>
                        <button
                            className="btn btn-ghost btn-sm update-bar__btn update-bar__btn--retry"
                            onClick={checkForUpdate}
                        >
                            <RefreshCw size={14} />
                            Retry
                        </button>
                        <button
                            className="btn btn-ghost btn-sm btn-icon update-bar__dismiss"
                            onClick={() => setDismissed(true)}
                            aria-label="Dismiss update notification"
                        >
                            <X size={14} />
                        </button>
                    </>
                ) : (
                    <>
                        {status === 'idle' && (
                            <button
                                className="btn btn-ghost btn-sm update-bar__btn update-bar__btn--primary"
                                onClick={handleUpdate}
                            >
                                <Download size={14} />
                                Update now
                            </button>
                        )}

                        {status === 'downloading' && (
                            <span className="update-bar__status">
                                <RefreshCw size={14} className="update-bar__spinner" />
                                Downloading…
                            </span>
                        )}

                        {status === 'installing' && (
                            <span className="update-bar__status">
                                <RefreshCw size={14} className="update-bar__spinner" />
                                Installing… Restarting shortly
                            </span>
                        )}

                        {status === 'error' && (
                            <>
                                <span className="update-bar__status update-bar__status--error">
                                    Couldn't install the update
                                </span>
                                <button
                                    className="btn btn-ghost btn-sm update-bar__btn update-bar__btn--retry"
                                    onClick={() => { setStatus('idle'); }}
                                >
                                    Retry
                                </button>
                            </>
                        )}

                        {status === 'idle' && (
                            <button
                                className="btn btn-ghost btn-sm btn-icon update-bar__dismiss"
                                onClick={() => setDismissed(true)}
                                aria-label="Dismiss update notification"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
